/**
 * AssignLockerModal — single source of truth for assigning a locker to a
 * pickup order. Used by both /store/orders (StoreOrders.tsx) and
 * /store/pickup-orders (PickupOrders.tsx) so the two staff workflows
 * stay in lockstep.
 *
 * Design pivot (replaces the tab-toggle UI from commit 9c06e774):
 *   The Eufy sidecar's auto-passcode-create P2P call times out reliably
 *   on T8502 keypads. Rather than offering staff "Auto vs Manual" and
 *   forcing them to wait for the timeout on every assignment, we just
 *   skip the sidecar entirely: server picks a fresh 6-digit code, staff
 *   types it into the Eufy phone app, ticks the confirmation box, hits
 *   submit. One flow, no waiting, no half-states.
 *
 *   The sidecar's /api/store/pickup-orders/<id>/assign-locker/ endpoint
 *   still exists but no UI calls it anymore — kept warm for when the
 *   P2P issue is solved upstream.
 *
 * Props are deliberately narrow:
 *   - order: { id; order_number; customer_name } — the only fields we
 *     actually display. Both PickupOrder and the StoreOrders flavor
 *     satisfy this shape.
 *   - lockers: list of available lockers. Caller is responsible for
 *     filtering occupied ones; we render whatever's passed.
 *   - onSuccess: called with the server response after a successful
 *     assignment. Parent typically refetches orders + lockers + shows
 *     a toast.
 *   - onClose: dismisses the modal. Parent owns visibility state.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './AssignLockerModal.css';

export interface AssignLockerModalOrder {
  id: number;
  order_number: string;
  customer_name: string;
}

export interface AssignLockerModalLocker {
  id: number;
  number: number | string;
  name: string;
  is_occupied: boolean;
}

export interface AssignLockerModalResult {
  success: boolean;
  message: string;
  passcode: string;
  locker_number: number | string;
  locker_name: string;
  customer_email: string | null;
  /**
   * Whether the server actually SENT the locker-code email. Distinct from
   * customer_email, which only says an address exists — a send can fail with
   * a perfectly good address on file. The caller must key its "customer
   * notified" wording off THIS, or it will tell staff the customer was told
   * when they were not, and nobody phones them.
   * Undefined only if an endpoint predates reporting it.
   */
  email_sent?: boolean;
}

interface Props {
  order: AssignLockerModalOrder;
  lockers: AssignLockerModalLocker[];
  onSuccess: (result: AssignLockerModalResult) => void;
  onClose: () => void;
  /**
   * Where the confirmed locker + code get POSTed. Defaults to the manual
   * assignment endpoint, which is what plain "assign a locker to an order
   * that is already a locker order" means.
   *
   * The in-store -> locker SWITCH needs a different endpoint: the assign
   * endpoints 400 unless pickup_method is already 'locker', and the switch
   * is what performs that flip. Same picker, same code-confirmation step,
   * different destination — so this is a prop rather than a second modal.
   */
  endpoint?: string;
  /** Modal heading. Defaults to "Assign Locker". */
  title?: string;
  /** Primary button label (non-submitting state). */
  submitLabel?: string;
  /** Label while the POST is in flight. */
  submittingLabel?: string;
  /** Heading on the post-success screen. */
  successTitle?: string;
}

const AssignLockerModal: React.FC<Props> = ({
  order,
  lockers,
  onSuccess,
  onClose,
  endpoint,
  title = 'Assign Locker',
  submitLabel = 'Assign Locker & Email Customer',
  submittingLabel = 'Assigning…',
  successTitle = 'Locker Assigned',
}) => {
  const [selectedLockerId, setSelectedLockerId] = useState<number | null>(null);
  const [generatedCode, setGeneratedCode] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [codeError, setCodeError] = useState<string>('');
  const [codeConfirmed, setCodeConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [result, setResult] = useState<AssignLockerModalResult | null>(null);

  const fetchNewCode = useCallback(async () => {
    setGenerating(true);
    setCodeError('');
    // Regenerating invalidates the previous confirmation — the staffer
    // hasn't typed the new code into the Eufy app yet.
    setCodeConfirmed(false);
    try {
      // NOTE: no leading /api — the axios client's baseURL already
      // includes it (see services/api.ts API_BASE_URL). Prefixing /api
      // here produces /api/api/... → 404.
      const response = await api.get('/store/lockers/generate-code/');
      setGeneratedCode(response.data.code);
    } catch (err: any) {
      setCodeError(err.response?.data?.error || 'Could not fetch a code. Try again.');
    } finally {
      setGenerating(false);
    }
  }, []);

  useEffect(() => {
    fetchNewCode();
  }, [fetchNewCode]);

  const handleSubmit = async () => {
    if (!selectedLockerId || !codeConfirmed || !generatedCode) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      // No leading /api — baseURL already has it (see fetchNewCode note).
      const url = endpoint ?? `/store/pickup-orders/${order.id}/assign-locker-manual/`;
      const response = await api.post(
        url,
        { locker_id: selectedLockerId, passcode: generatedCode },
      );
      const payload: AssignLockerModalResult = {
        success: true,
        message: response.data.message,
        passcode: response.data.assignment?.passcode || generatedCode,
        locker_number: response.data.assignment?.locker_number,
        locker_name: response.data.assignment?.locker_name,
        customer_email: response.data.customer_email ?? null,
        email_sent: response.data.email_sent,
      };
      setResult(payload);
      onSuccess(payload);
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to assign locker. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // If submission succeeded, render the confirmation screen instead of
  // the form. The parent gets notified via onSuccess and chooses when
  // to close the modal (via onClose); we just freeze the UI in a
  // success state to prevent accidental double-submits.
  if (result) {
    return (
      <div className="alm-overlay" onClick={onClose} role="dialog" aria-label={successTitle}>
        <div className="alm-modal" onClick={e => e.stopPropagation()}>
          <div className="alm-success">
            <div className="alm-success-icon" aria-hidden="true">✓</div>
            <h2>{successTitle}</h2>
            <p className="alm-success-summary">
              Locker {result.locker_number} · Code <code>{result.passcode}</code>
            </p>
            {result.customer_email && (
              <p className="alm-success-email">
                Customer notified at <strong>{result.customer_email}</strong>.
              </p>
            )}
            <button className="alm-btn alm-btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="alm-overlay"
      onClick={() => !submitting && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="alm-modal" onClick={e => e.stopPropagation()}>
        <header className="alm-header">
          <h2>{title}</h2>
          <button
            className="alm-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="alm-body">
          <p className="alm-order">
            Order <strong>{order.order_number}</strong> · {order.customer_name}
          </p>

          {/* Step 1 — locker */}
          <section className="alm-section">
            <h3 className="alm-section-title">1. Choose a locker</h3>
            <div className="alm-locker-grid">
              {lockers.length === 0 && (
                <p className="alm-empty">No lockers available.</p>
              )}
              {lockers.map(locker => (
                <button
                  key={locker.id}
                  type="button"
                  className={[
                    'alm-locker',
                    locker.is_occupied ? 'alm-locker-busy' : 'alm-locker-free',
                    selectedLockerId === locker.id ? 'alm-locker-selected' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => !locker.is_occupied && setSelectedLockerId(locker.id)}
                  disabled={locker.is_occupied || submitting}
                  aria-pressed={selectedLockerId === locker.id}
                >
                  <div className="alm-locker-num">#{locker.number}</div>
                  <div className="alm-locker-name">{locker.name}</div>
                  {locker.is_occupied && <div className="alm-locker-tag">IN USE</div>}
                </button>
              ))}
            </div>
          </section>

          {/* Step 2 — code + Eufy app confirmation */}
          <section className="alm-section">
            <h3 className="alm-section-title">2. Program this code in the Eufy app</h3>

            <div className="alm-code-display">
              {generating ? (
                <span className="alm-code-loading">Getting a code…</span>
              ) : codeError ? (
                <span className="alm-code-error">{codeError}</span>
              ) : (
                <span className="alm-code-value" aria-label="Generated code">
                  {generatedCode}
                </span>
              )}
              <button
                type="button"
                className="alm-regen"
                onClick={fetchNewCode}
                disabled={generating || submitting}
              >
                Regenerate
              </button>
            </div>

            <p className="alm-instructions">
              Open the Eufy app, select Locker
              {selectedLockerId
                ? ` #${lockers.find(l => l.id === selectedLockerId)?.number ?? ''}`
                : ' you just selected'},
              and enter this code as a new user passcode. Then check the box
              below to confirm.
            </p>

            <label className="alm-confirm">
              <input
                type="checkbox"
                checked={codeConfirmed}
                onChange={e => setCodeConfirmed(e.target.checked)}
                disabled={generating || submitting || !generatedCode}
              />
              <span>I have entered this code into the Eufy app.</span>
            </label>
          </section>

          {submitError && (
            <div className="alm-submit-error" role="alert">{submitError}</div>
          )}
        </div>

        <footer className="alm-footer">
          <button
            type="button"
            className="alm-btn alm-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="alm-btn alm-btn-primary"
            onClick={handleSubmit}
            disabled={
              !selectedLockerId
              || !codeConfirmed
              || !generatedCode
              || submitting
            }
          >
            {submitting ? submittingLabel : submitLabel}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AssignLockerModal;
